import { Injectable } from '@angular/core';
import {
  Firestore,
  collection,
  query,
  where,
  getDocs,
  updateDoc,
  doc,
  Timestamp,
} from '@angular/fire/firestore';
import { BookingLockService } from './booking-lock.service';

@Injectable({
  providedIn: 'root',
})
export class BookingAutoRejectService {
  constructor(
    private afs: Firestore,
    private bookingLock: BookingLockService,
  ) {}

  /**
   * Checks for "Pending" bookings created more than 30 minutes ago and marks them as "Rejected".
   */
  async checkAndRejectBookings() {
    const thirtyMinutesAgo = new Date(Date.now() - 30 * 60 * 1000);
    const bookingRef = collection(this.afs, 'booking-list');

    // We can't easily do a range query on createdAt with where status == 'Pending'
    // without a composite index, so we'll query for Pending and filter locally
    // or just query by status and check manually for safety if index is missing.
    const q = query(bookingRef, where('status', '==', 'Pending'));

    try {
      const snapshot = await getDocs(q);
      const updates = [];

      for (const docSnap of snapshot.docs) {
        const data = docSnap.data();
        const createdAt = data['createdAt'] as Timestamp;

        if (createdAt && createdAt.toDate() < thirtyMinutesAgo) {
          const docRef = doc(this.afs, 'booking-list', docSnap.id);
          updates.push(
            updateDoc(docRef, {
              status: 'Rejected',
              rejectionReason:
                'Auto-rejected: Vendor did not respond within 30 minutes.',
            }),
          );

          // Unlock the vehicle in Realtime DB
          if (
            data['equipmentId'] &&
            data['customerId'] &&
            data['startDate'] &&
            data['endDate']
          ) {
            const s = data['startDate'].toDate
              ? data['startDate'].toDate()
              : new Date(data['startDate']);
            const e = data['endDate'].toDate
              ? data['endDate'].toDate()
              : new Date(data['endDate']);

            // We don't await this individually to keep updates parallel,
            // but we should catch errors so one failure doesn't stop others?
            // Actually, promise.all below waits for updates. unlockVehicle is separate.
            // We should probably add it to a list of promises if we want to be sure it runs.
            // But strictly, modifying RDB is separate from Firestore.
            // To be safe, let's just fire and forget or await it if we want to be strict.
            // Given we accumulate 'updates', let's just run it.
            // Since unlockVehicle is async, we should await it or push to tasks.
            // But 'updates' array expects Firestore Promises (from updateDoc).
            // Let's just call it.
            this.bookingLock
              .unlockVehicle(data['equipmentId'], s, e, data['customerId'])
              .catch((err) =>
                console.error('Failed to auto-unlock vehicle:', err),
              );
          }
        }
      }

      if (updates.length > 0) {
        await Promise.all(updates);
        console.log(`Successfully auto-rejected ${updates.length} bookings.`);
      }
    } catch (error) {
      console.error('Error in BookingAutoRejectService:', error);
    }
  }
}
